import React, { useMemo } from 'react';
import type { FunnelCaptureConfig, LeadCaptureFieldMode, LeadCaptureFields } from '@troia-v3/types';
import { ProviderId, resolveLeadCaptureFields } from '@troia-v3/types';

import { Select, Switch, Textarea } from '@/shared/components/ui';
import { useFormsDropdown } from '@/shared/hooks/useFormsDropdown';
import { useChannelsDropdown } from '@/modules/channels/hooks/useChannelsDropdown';
import { Icon } from '@/shared/icons';

/**
 * Aba "Captura" do drawer de funis — conteúdo do mockup lote-b/11, mas com o
 * LAYOUT do formulário de edição de canal (decisão do dono 22/08): campos um
 * abaixo do outro, rótulo `text-sm font-medium`, ajuda `text-xs`, linha de
 * toggle com rótulo/descrição à esquerda e o Switch à direita. Nada lado a lado.
 *
 * Estado é do PAI (`FunnelsSettingsDrawer.funnelForm.capture`): `null` =
 * desligada (o PUT manda `capture: null` → `$unset`), objeto = ligada. A aba
 * só desenha e edita. Hooks de dropdown gateados por `enabled` (SEMPRE #10):
 * o drawer monta a aba só quando ativa, mas o `isOpen` real é repassado.
 * O drawer força o tema claro via override das CSS variables — por isso as
 * cores aqui são `var(--theme-*)`, nunca hex.
 */
export interface FunnelCaptureTabProps {
  capture: FunnelCaptureConfig | null;
  onChange: (capture: FunnelCaptureConfig | null) => void;
  /** `isOpen` do drawer — gate dos dropdowns. */
  enabled: boolean;
}

/** Código de exemplo do preview — formato real `CAT-XXXX` (spec §4.1). */
const PREVIEW_CODE = 'CAT-4F7K';
/** Cupom de exemplo do preview — número da empresa com 4 dígitos. */
const PREVIEW_COUPON = '0012';
/** `{{code}}` e `{{cupom}}` na mensagem — o grupo mantém a variável no split. */
const MESSAGE_VARIABLE = /(\{\{\s*(?:code|cupom)\s*\}\})/i;

const DEFAULT_MESSAGE = 'Olá! Quero participar da promoção. Código {{code}}';
const DEFAULT_CONSENT = 'Autorizo a {{empresa}} a entrar em contato por WhatsApp e concordo com a política de privacidade.';

const WHATSAPP_PROVIDERS: ReadonlySet<string> = new Set<string>([ProviderId.WHATSAPP_BUSINESS, ProviderId.GATEWAY_WHATSAPP]);

const labelClass = 'block text-sm font-medium mb-2';
const labelStyle: React.CSSProperties = { color: 'var(--theme-card-text)' };
const helpClass = 'text-xs';
const helpStyle: React.CSSProperties = { color: 'var(--theme-card-muted)', lineHeight: 1.55 };
const sectionStyle: React.CSSProperties = { borderTop: '1px solid var(--theme-card-border)', paddingTop: 16, marginTop: 16 };

type CaptureFieldKey = keyof LeadCaptureFields;

/** Campos da página que o gestor controla — nome e WhatsApp são fixos. */
const CAPTURE_FIELD_ROWS: ReadonlyArray<{ field: CaptureFieldKey; label: string; description: string; pageName: string }> = [
  { field: 'email', label: 'E-mail', description: 'Vai para o lead e para o contato.', pageName: 'e-mail' },
  { field: 'cpf', label: 'CPF', description: 'Com máscara e conferência dos dígitos. Vai para o cadastro do contato.', pageName: 'CPF' },
  { field: 'birthDate', label: 'Data de nascimento', description: 'Vai para o cadastro do contato (usada nos aniversários).', pageName: 'data de nascimento' },
];

/**
 * Uma linha por campo com dois Switches: "Exibir" e "Obrigatório". Obrigatório
 * só liga com o campo exibido; desligar "Exibir" esconde e solta a obrigação.
 */
const CaptureFieldRow: React.FC<{ field: CaptureFieldKey; label: string; description: string; mode: LeadCaptureFieldMode; onChange: (mode: LeadCaptureFieldMode) => void }> = ({ field, label, description, mode, onChange }) => {
  const shown = mode !== 'hidden';
  return (
    <div className="flex items-start justify-between gap-4" data-testid={`funnel-capture-field-${field}`}>
      <div className="flex-1 min-w-0">
        <div className="block text-sm font-medium mb-1" style={labelStyle}>{label}</div>
        <p className={helpClass} style={helpStyle}>{description}</p>
      </div>
      <div className="flex items-start gap-4 flex-none">
        <div className="flex flex-col items-center">
          <span className={helpClass} style={helpStyle}>Exibir</span>
          <Switch size="sm" checked={shown} onChange={(on) => onChange(on ? 'optional' : 'hidden')} aria-label={`Exibir ${label}`} data-testid={`funnel-capture-field-${field}-show`} />
        </div>
        <div className="flex flex-col items-center">
          <span className={helpClass} style={helpStyle}>Obrigatório</span>
          <Switch size="sm" checked={mode === 'required'} disabled={!shown} onChange={(on) => onChange(on ? 'required' : 'optional')} aria-label={`${label} obrigatório`} data-testid={`funnel-capture-field-${field}-required`} />
        </div>
      </div>
    </div>
  );
};

/** Linha de toggle no padrão do form de canal: rótulo + descrição à esquerda, Switch à direita. */
const ToggleRow: React.FC<{ id: string; label: string; description: React.ReactNode; checked: boolean; onChange: (v: boolean) => void; testId: string }> = ({ id, label, description, checked, onChange, testId }) => (
  <div className="flex items-start justify-between gap-4">
    <div className="flex-1 min-w-0">
      <label htmlFor={id} className="block text-sm font-medium mb-1" style={labelStyle}>{label}</label>
      <p className={helpClass} style={helpStyle}>{description}</p>
    </div>
    <Switch id={id} checked={checked} onChange={onChange} aria-label={label} data-testid={testId} />
  </div>
);

export const FunnelCaptureTab: React.FC<FunnelCaptureTabProps> = ({ capture, onChange, enabled }) => {
  const isOn = capture !== null;

  const { data: forms = [] } = useFormsDropdown('form', enabled && isOn);
  const { data: channels = [] } = useChannelsDropdown({ enabled: enabled && isOn });

  const channelOptions = useMemo(
    () =>
      channels
        // Achado 2 (fix round 1): filtro ESTRITO — só canais WhatsApp de fato
        // (`whatsapp-business` / `gateway-whatsapp`). Canal sem `providerId`
        // não é mais aceito por omissão; a lista de confirmação é só WhatsApp.
        .filter((channel) => !!channel.providerId && WHATSAPP_PROVIDERS.has(channel.providerId))
        .map((channel) => ({ value: channel.id, label: `${channel.name} · ${channel.identifier}` })),
    [channels],
  );

  const message = capture?.message ?? '';
  /** Fix round 1 (Achado 3): vazio usa a mensagem padrão do sistema — o rótulo
   *  "Mensagem padrão" deixa isso explícito na tela em vez de fingir que o
   *  texto default foi digitado pelo usuário. */
  const usingDefaultMessage = message === '';
  // Fix round 1 (Achado 1): o código vira um segmento `<b>` mono/negrito
  // dentro da prévia — nunca texto plano interpolado por replace de string.
  // `{{cupom}}` só vira número quando o funil dá cupom; sem isso a página
  // pública apaga a variável, e a prévia faz o mesmo.
  const issueCoupon = capture?.issueCoupon === true;
  const previewParts = (message || DEFAULT_MESSAGE).split(MESSAGE_VARIABLE);

  const patch = (partial: Partial<FunnelCaptureConfig>) => {
    if (capture === null) return;
    const next: FunnelCaptureConfig = { ...capture, ...partial };
    // Campo apagado sai do objeto (o Zod `.strict()` aceita ausência, não `undefined` literal serializado).
    (Object.keys(next) as Array<keyof FunnelCaptureConfig>).forEach((key) => {
      if (next[key] === undefined || next[key] === '') delete next[key];
    });
    onChange(next);
  };

  // Campos da página (2026-10-21): o legado `emailRequired` é lido pelo
  // resolvedor e some do objeto na primeira edição — dali em diante vale `fields`.
  const fields = resolveLeadCaptureFields(capture);
  const setFieldMode = (field: CaptureFieldKey, mode: LeadCaptureFieldMode) => {
    if (capture === null) return;
    const next: FunnelCaptureConfig = { ...capture, fields: { ...fields, [field]: mode } };
    delete next.emailRequired;
    onChange(next);
  };
  const pageFieldNames = ['nome', 'WhatsApp', ...CAPTURE_FIELD_ROWS.filter((row) => fields[row.field] !== 'hidden').map((row) => row.pageName), 'aceite'].join(', ');

  const toggle = (checked: boolean) => {
    // Fix (revisão final Parte 1): só `message` é pré-preenchido. `consentText`
    // fica AUSENTE — "vazio = padrão do sistema com o nome real da empresa"
    // (o `placeholder` do textarea já mostra `DEFAULT_CONSENT` como ajuda).
    // Pré-preencher com `DEFAULT_CONSENT` gravava a variável `{{empresa}}`
    // literal no banco, e a página pública exibiria "Autorizo a {{empresa}}…".
    onChange(checked ? { message: DEFAULT_MESSAGE } : null);
  };

  return (
    <div data-testid="funnel-capture-tab">
      {/* Linha do switch — igual nas duas variantes, muda só o texto de apoio */}
      <ToggleRow
        id="funnel-capture-enabled"
        label="Captura por QR Code neste funil"
        description={isOn
          ? <>Atendentes deste funil ganham o botão <b>Captura QR</b> no CRM e em Minhas Vendas. O lead entra na primeira etapa, já com o vendedor como responsável.</>
          : <>Ligue para escolher o formulário, o canal de confirmação e o texto da página pública.</>}
        checked={isOn}
        onChange={toggle}
        testId="funnel-capture-enabled"
      />
      {/* D5 (decisão do coordenador): captura ligada exige `allowDuplicateContacts:false`
          no funil — o backend rejeita 422 se religarem duplicados com a captura ativa
          (força aplicada no pai, `FunnelsSettingsDrawer`, na transição off→on do switch). */}
      <p className={`${helpClass} mt-1.5`} style={helpStyle} data-testid="funnel-capture-duplicates-hint">
        A captura por QR desliga contatos duplicados neste funil.
      </p>

      {!isOn && (
        <>
          <div style={sectionStyle}>
            <div
              className="flex items-start gap-2.5 rounded-[10px] px-3 py-2.5 text-xs leading-relaxed"
              style={{ backgroundColor: 'var(--theme-warning-subtle)', color: 'var(--theme-warning-fg)' }}
              data-testid="funnel-capture-off-warning"
            >
              <Icon name="alert-triangle" size={16} className="flex-none mt-0.5" />
              <span>Com a captura desligada, o botão <b>Captura QR</b> some para os atendentes deste funil e os QR Codes já gerados param de funcionar.</span>
            </div>
          </div>
          <div style={sectionStyle}>
            <div className="flex items-start gap-3 rounded-[12px] p-3.5" style={{ backgroundColor: 'var(--theme-surface-sunken)', border: '1px solid var(--theme-card-border)' }}>
              <span className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-none" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
                <Icon name="qr-code" size={18} />
              </span>
              <div>
                <div className="text-sm font-semibold" style={labelStyle}>Como funciona</div>
                <p className={`${helpClass} mt-0.5`} style={helpStyle}>
                  Vendedor gera o QR → cliente preenche nome e WhatsApp no celular → confirma pelo WhatsApp → vendedor responde o formulário → pontos e ranking. Tudo configurado aqui, por funil.
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {isOn && (
        <>
          {/* Formulário de captura */}
          <div style={sectionStyle}>
            <label className={labelClass} style={labelStyle}>Formulário de captura</label>
            <Select
              value={capture.formId ?? ''}
              onChange={(value) => patch({ formId: value || undefined })}
              // Achado 1 (fix round 1): "Nenhum" vai por ÚLTIMO, como no mockup
              // (linha 21 de 11-funil-aba-captura.html) e como já era feito no
              // select de canal ("Sem confirmação" por último).
              options={[...forms, { value: '', label: 'Nenhum (só nome e WhatsApp)' }]}
              data-testid="funnel-capture-form"
            />
            <p className={`${helpClass} mt-2`} style={helpStyle}>
              Modelo criado em <b>Automação › Formulários</b>. O vendedor responde depois da confirmação; as perguntas de escolha viram filtros no CRM.{' '}
              {capture.formId && (
                <a
                  href={`/automation/forms/${capture.formId}/edit`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 font-semibold"
                  style={{ color: 'var(--theme-primary)' }}
                  data-testid="funnel-capture-open-form"
                >
                  Abrir modelo <Icon name="external-link" size={12} />
                </a>
              )}
            </p>
          </div>

          {/* Confirmação no WhatsApp */}
          <div style={sectionStyle}>
            <label className={labelClass} style={labelStyle}>Confirmação no WhatsApp</label>
            <Select
              value={capture.channelId ?? ''}
              onChange={(value) => patch({ channelId: value || undefined })}
              options={[...channelOptions, { value: '', label: 'Sem confirmação' }]}
              data-testid="funnel-capture-channel"
            />
            <p className={`${helpClass} mt-2`} style={helpStyle}>
              Com um canal escolhido, a página do cliente abre o WhatsApp com a mensagem pronta. Quando a mensagem chega, a captura é confirmada e só então pontua. <b>Sem canal, a captura conta na hora.</b>
            </p>
          </div>

          {/* Cupom de sorteio (2026-09-29): número da empresa em sequência, dado no cadastro */}
          <div style={sectionStyle}>
            <ToggleRow
              id="funnel-capture-issue-coupon"
              label="Dar cupom de sorteio a quem se cadastrar"
              description={<>Cada cadastro novo ganha o próximo número da empresa (0001, 0002…), que fica nos cupons do lead e aparece para o cliente na página. Quem já tinha lead aberto neste funil não ganha outro.</>}
              checked={issueCoupon}
              onChange={(checked) => patch({ issueCoupon: checked ? true : undefined })}
              testId="funnel-capture-issue-coupon"
            />
          </div>

          {/* Mensagem pré-preenchida — abaixo, nunca ao lado (padrão do form de canal) */}
          <div style={sectionStyle}>
            <label className={labelClass} style={labelStyle}>Mensagem pré-preenchida</label>
            <Textarea
              value={message}
              onChange={(event) => patch({ message: event.target.value })}
              rows={3}
              resize="vertical"
              placeholder={DEFAULT_MESSAGE}
              data-testid="funnel-capture-message"
            />
            <p className={`${helpClass} mt-2`} style={helpStyle}>
              Use <code className="rounded px-1.5 py-px" style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-primary)' }}>{'{{code}}'}</code> para o código do atendimento — é por ele que ligamos a mensagem ao vendedor certo.
              {issueCoupon && (
                <> Use <code className="rounded px-1.5 py-px" style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-primary)' }}>{'{{cupom}}'}</code> para o número do cupom de sorteio.</>
              )}
            </p>
            <label className={`${labelClass} mt-4`} style={labelStyle}>Como o cliente vê</label>
            {/* Achado 1 (fix round 1): balão fiel ao `.preview-bubble` do
                mockup (`qr-b.css`) — cantos 12/12/12/2, max-width 360,
                #1f2937, 13px, padding 10px 12px. O código vem em `<b>`
                mono/negrito (`.preview-bubble b`), nunca texto plano.
                Verde do balão = cor de produto do WhatsApp (prévia), não do tema. */}
            <div
              style={{
                backgroundColor: '#dcf8c6',
                color: '#1f2937',
                borderRadius: '12px 12px 12px 2px',
                padding: '10px 12px',
                fontSize: 13,
                lineHeight: 1.45,
                maxWidth: 360,
              }}
              data-testid="funnel-capture-preview"
            >
              {previewParts.map((part, index) => {
                // Índices ímpares são as variáveis capturadas pelo split.
                if (index % 2 === 0) return <React.Fragment key={index}>{part}</React.Fragment>;
                if (/cupom/i.test(part)) {
                  return issueCoupon ? <b key={index} className="font-mono font-semibold">{PREVIEW_COUPON}</b> : null;
                }
                return <b key={index} className="font-mono font-semibold">{PREVIEW_CODE}</b>;
              })}
            </div>
            {/* Achado 3 (fix round 1): rótulo explícito quando a prévia usa
                a mensagem padrão do sistema (textarea vazia) — nunca deixa o
                usuário pensar que aquele texto foi digitado por ele. */}
            {usingDefaultMessage && (
              <p className={`${helpClass} mt-1`} style={helpStyle} data-testid="funnel-capture-preview-default">
                Mensagem padrão
              </p>
            )}
          </div>

          {/* Campos da página pública — exibir e obrigatório, um par por campo */}
          <div style={sectionStyle}>
            <label className={labelClass} style={labelStyle}>Campos da página</label>
            <p className={helpClass} style={helpStyle}>Nome e WhatsApp são sempre pedidos. Quem já é contato tem o cadastro atualizado com o que digitar.</p>
            <div className="mt-3 space-y-3" data-testid="funnel-capture-fields">
              {CAPTURE_FIELD_ROWS.map((row) => (
                <CaptureFieldRow
                  key={row.field}
                  field={row.field}
                  label={row.label}
                  description={row.description}
                  mode={fields[row.field]}
                  onChange={(mode) => setFieldMode(row.field, mode)}
                />
              ))}
            </div>
          </div>

          {/* Página pública — consentimento */}
          <div style={sectionStyle}>
            <label className={labelClass} style={labelStyle}>Texto de consentimento (LGPD)</label>
            <Textarea
              value={capture.consentText ?? ''}
              onChange={(event) => patch({ consentText: event.target.value })}
              rows={3}
              resize="vertical"
              placeholder={DEFAULT_CONSENT}
              data-testid="funnel-capture-consent"
            />
            <div className="flex items-start gap-3 rounded-[12px] p-3.5 mt-4" style={{ backgroundColor: 'var(--theme-surface-sunken)', border: '1px solid var(--theme-card-border)' }}>
              <span className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-none" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
                <Icon name="smartphone" size={18} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold" style={labelStyle}>A página usa a marca da empresa</div>
                <p className={`${helpClass} mt-0.5`} style={helpStyle}>
                  Logo e cor primária vêm do cadastro da empresa (Configurações › Empresa). Campos: {pageFieldNames}. Sem login, sem banner de instalar o app.
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

FunnelCaptureTab.displayName = 'FunnelCaptureTab';
